import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Pencil } from 'lucide-react';
import { SelectionPanelProps } from './types';
import { OptionCard } from './OptionCard';

export const SelectionPanel: React.FC<SelectionPanelProps> = ({
  isOpen,
  type,
  options,
  currentValue,
  onSelect,
  onEditManually,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const title = type === 'title' ? 'Select Episode Title' : 'Select Episode Description';

  const handleSelect = (id: string, text: string) => {
    setSelectedId(id);
    setTimeout(() => {
      onSelect(text);
      setSelectedId(null);
    }, 150);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{
            duration: 0.3,
            ease: [0.32, 0.72, 0, 1],
          }}
          className="absolute top-0 right-0 bottom-0 w-1/2 z-50 bg-[var(--pm-bg-deeper)] flex flex-col rounded-r-lg"
        >
          {/* Header */}
          <div className="flex items-center gap-2 px-5 py-4 border-b border-[var(--pm-border-default)]">
            <Sparkles className="h-4 w-4 text-yellow-500" />
            <h3 className="text-sm font-medium text-[var(--pm-text-primary)]">{title}</h3>
          </div>

          {/* Options List */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
            {/* Current Value Option */}
            <OptionCard
              text={currentValue || 'No value set'}
              badge="current"
              isSelected={selectedId === 'current'}
              onClick={() => handleSelect('current', currentValue || '')}
            />

            {/* AI Options */}
            {options.map((option, index) => (
              <OptionCard
                key={option.id}
                text={option.text}
                badge={index + 1}
                isSelected={selectedId === option.id}
                onClick={() => handleSelect(option.id, option.text)}
              />
            ))}

            {/* Write Your Own Option */}
            <button
              onClick={onEditManually}
              className="w-full p-3 rounded-lg border-2 border-dashed border-[var(--pm-border-default)] hover:border-cyan-500 hover:bg-cyan-500/5 transition-all text-left"
            >
              <div className="flex items-center gap-3">
                <div className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full bg-[var(--pm-bg-card)]">
                  <Pencil className="h-4 w-4 text-[var(--pm-text-faint)]" />
                </div>
                <span className="text-sm text-[var(--pm-text-faint)]">Write your own...</span>
              </div>
            </button>
          </div>

          {/* Footer hint */}
          <div className="px-5 py-3 border-t border-[var(--pm-border-default)]">
            <p className="text-xs text-[var(--pm-text-faint)] text-center">
              Click an option to select it
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
